// ComplianceReview (TASK-583) — the document-based FinSA/FINMA/AML review the
// reborn review_compliance skill writes (rule engine over the client's documents,
// honest outcome/score, violations, regulations checked), rendered as a canvas
// tile. Self-fetches by clientId via complianceApiService. Honest, never
// fake-green: a null score renders gray "not scored", not a pass.
import { useQuery } from '@tanstack/react-query';
import {
  complianceApiService,
  type ComplianceReviewLatest,
} from '../../../services/api/complianceApiService';
import { WidgetContainer } from '../WidgetContainer';

type Tone = 'ok' | 'warn' | 'bad' | 'neutral';

const TONE_CLASS: Record<Tone, string> = {
  ok: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  bad: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  neutral: 'bg-accent text-muted-foreground',
};

// Honest badge: violations override; a null score is "not scored" (gray), never
// green. Mirrors the brief's format_compliance_review model.
export function deriveReviewBadge(
  review: { compliance_score: number | null; violations_found: string[]; compliance_outcome: string | null } | null,
): { tone: Tone; label: string } {
  if (!review) return { tone: 'neutral', label: 'No review' };
  const n = review.violations_found?.length ?? 0;
  if (n > 0 || review.compliance_outcome === 'issues_found')
    return { tone: 'bad', label: `${n || 'Issues'} gap${n === 1 ? '' : 's'} found` };
  const score = review.compliance_score;
  if (score === null || score === undefined)
    return { tone: 'neutral', label: 'Not scored — manual review' };
  if (score >= 80) return { tone: 'ok', label: 'Compliant' };
  if (score >= 60) return { tone: 'warn', label: 'Minor issues' };
  return { tone: 'bad', label: 'Action required' };
}

export function ComplianceReview({ clientId }: { clientId: string }) {
  const { data, isLoading, isError } = useQuery<ComplianceReviewLatest>({
    queryKey: ['compliance-review', clientId],
    queryFn: () => complianceApiService.getLatestComplianceReview(clientId),
    enabled: !!clientId,
  });

  const review = data?.review ?? null;
  const badge = deriveReviewBadge(review);
  const regs = review?.regulatory_intelligence?.regulations_checked ?? [];

  return (
    <WidgetContainer
      title="Compliance review"
      source="Compliance · AI review"
      // TASK-825: when the review was generated, not when the tile mounted.
      dataAsOf={review?.generated_at ?? null}
    >
      {isLoading && <div className="h-24 w-full animate-pulse rounded bg-accent" />}
      {isError && (
        <div className="text-[13px] text-muted-foreground">Could not load the compliance review.</div>
      )}
      {!isLoading && !isError && !review && (
        <div className="text-[13px] text-muted-foreground">
          No compliance review for this client yet — run a compliance review from a meeting's
          prepared documents.
        </div>
      )}
      {review && (
        <div>
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${TONE_CLASS[badge.tone]}`}>
              {badge.label}
            </span>
            {typeof review.compliance_score === 'number' && (
              <span className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-medium text-foreground">
                score {review.compliance_score}
              </span>
            )}
            {review.compliance_outlook && (
              <span className="ml-auto text-[11px] text-muted-foreground">{review.compliance_outlook}</span>
            )}
          </div>

          <p className="whitespace-pre-line text-[13px] text-muted-foreground">
            {review.compliance_review}
          </p>

          {review.violations_found?.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Regulatory gaps
              </div>
              <ul className="mt-1 list-disc pl-4 text-[12px] text-red-700 dark:text-red-300">
                {review.violations_found.slice(0, 6).map((v, i) => (
                  <li key={i}>{v}</li>
                ))}
              </ul>
            </div>
          )}

          {review.recommendations?.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Recommendations
              </div>
              <ul className="mt-1 list-disc pl-4 text-[12px] text-muted-foreground">
                {review.recommendations.slice(0, 5).map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}

          {regs.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {regs.map((r) => (
                <span
                  key={r.id}
                  title={typeof r.score === 'number' ? `score ${r.score}` : undefined}
                  className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                >
                  {r.name}
                </span>
              ))}
            </div>
          )}

          {review.generated_at && (
            <div className="mt-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
              Reviewed {new Date(review.generated_at).toLocaleDateString()}
            </div>
          )}
        </div>
      )}
    </WidgetContainer>
  );
}
